import { useQuery, useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ErrorService } from "@/lib/errors/ErrorService";
import { useAuth } from "@/components/AuthProvider";
import { useCurrentCabinet } from "@/hooks/cabinet/useCabinetCore";
import type {
  Conversation,
  Message,
  CreateConversationRequest,
  CreateMessageRequest,
} from "@/types/api";
import type { Conversation as ChatConversation, ChatMessage } from "@/types/chat";
import type { ConversationArchiveFilter } from "@hector/shared-types";
import type {
  RenameConversationRequest,
  ResolvedConversationUserState,
  SetConversationArchivedRequest,
  SetConversationPinnedRequest,
} from "@hector/shared-types/conversations";
import { compareConversationProjectionOrder } from "@/utils/conversationProjection";

const GLOBAL_CONVERSATIONS_KEY = ["conversations", "global"] as const;
type OrganizationField = "pin" | "archive";

interface FieldGenerationState {
  latestGeneration: number;
  nextGeneration: number;
  pendingGenerations: Set<number>;
}

const organizationMutationGenerations = new WeakMap<object, Map<string, FieldGenerationState>>();
const organizationPendingMutations = new WeakMap<object, Map<string, Set<string>>>();

export interface GlobalConversationsOptions {
  archived?: ConversationArchiveFilter;
}

interface SetConversationPinnedInput extends SetConversationPinnedRequest {
  conversationId: string;
}

interface SetConversationArchivedInput extends SetConversationArchivedRequest {
  conversationId: string;
}

type OrganizationMutationInput = SetConversationPinnedInput | SetConversationArchivedInput;

interface OrganizationMutationContext {
  previous: Array<[QueryKey, ChatConversation[] | undefined]>;
  field: OrganizationField;
  generation: number;
  generationKey: string;
  mutationScopeKey: string;
  mutationKey: string;
  queryScopeKey: QueryKey | null;
  detailQueryKey: QueryKey | null;
}

/**
 * Keeps every global conversation snapshot isolated by the active cabinet,
 * authenticated user and archive projection. A user can switch cabinet without
 * briefly seeing the previous cabinet's personal archive state.
 */
export function globalConversationsQueryKey(
  cabinetId: string,
  userId: string,
  archived: ConversationArchiveFilter,
) {
  return [...GLOBAL_CONVERSATIONS_KEY, cabinetId, userId, archived] as const;
}

export function conversationDetailQueryKey(
  cabinetId: string,
  userId: string,
  conversationId: string,
) {
  return ["conversations", "detail", cabinetId, userId, conversationId] as const;
}

export function conversationMessagesQueryKey(
  cabinetId: string,
  userId: string,
  conversationId: string,
) {
  return ["messages", cabinetId, userId, conversationId] as const;
}

function globalConversationsScopeKey(cabinetId: string, userId: string) {
  return [...GLOBAL_CONVERSATIONS_KEY, cabinetId, userId] as const;
}

function getOrganizationMutationGenerations(queryClient: ReturnType<typeof useQueryClient>) {
  const existing = organizationMutationGenerations.get(queryClient);
  if (existing) return existing;

  const generations = new Map<string, FieldGenerationState>();
  organizationMutationGenerations.set(queryClient, generations);
  return generations;
}

function getOrganizationPendingMutations(queryClient: ReturnType<typeof useQueryClient>) {
  const existing = organizationPendingMutations.get(queryClient);
  if (existing) return existing;

  const pendingMutations = new Map<string, Set<string>>();
  organizationPendingMutations.set(queryClient, pendingMutations);
  return pendingMutations;
}

function startOrganizationPendingMutation(
  pendingMutations: Map<string, Set<string>>,
  mutationScopeKey: string,
  mutationKey: string,
): void {
  const pendingForConversation = pendingMutations.get(mutationScopeKey) ?? new Set<string>();
  pendingForConversation.add(mutationKey);
  pendingMutations.set(mutationScopeKey, pendingForConversation);
}

function settleOrganizationPendingMutation(
  pendingMutations: Map<string, Set<string>>,
  mutationScopeKey: string,
  mutationKey: string,
): boolean {
  const pendingForConversation = pendingMutations.get(mutationScopeKey);
  if (!pendingForConversation) return false;

  pendingForConversation.delete(mutationKey);
  if (pendingForConversation.size > 0) return false;

  pendingMutations.delete(mutationScopeKey);
  return true;
}

function startOrganizationMutationGeneration(
  generations: Map<string, FieldGenerationState>,
  generationKey: string,
): number {
  const state = generations.get(generationKey) ?? {
    latestGeneration: 0,
    nextGeneration: 0,
    pendingGenerations: new Set<number>(),
  };
  const generation = state.nextGeneration + 1;
  state.nextGeneration = generation;
  state.latestGeneration = generation;
  state.pendingGenerations.add(generation);
  generations.set(generationKey, state);
  return generation;
}

function isLatestOrganizationMutationGeneration(
  generations: Map<string, FieldGenerationState>,
  generationKey: string,
  generation: number,
): boolean {
  return generations.get(generationKey)?.latestGeneration === generation;
}

function settleOrganizationMutationGeneration(
  generations: Map<string, FieldGenerationState>,
  generationKey: string,
  generation: number,
): boolean {
  const state = generations.get(generationKey);
  if (!state) return false;

  state.pendingGenerations.delete(generation);
  // An older completion must not delete state still protecting a newer pending
  // mutation. Once all requests settle, a fresh mutation can start at generation
  // one because no earlier completion remains that could roll it back.
  if (state.pendingGenerations.size > 0) return false;
  generations.delete(generationKey);
  return true;
}

function archiveFilterForQueryKey(queryKey: QueryKey): ConversationArchiveFilter | null {
  const value = queryKey[4];
  return value === "exclude" || value === "only" || value === "include" ? value : null;
}

function asDate(value: string | null | undefined): Date | null {
  return value ? new Date(value) : null;
}

/**
 * Transform API conversation format to Chat conversation format
 */
function transformConversation(apiConv: Conversation): ChatConversation {
  return {
    id: apiConv.id,
    caseId: apiConv.case_id || null,
    caseTitle: apiConv.case_name ?? null,
    title: apiConv.conversation_title,
    summary: undefined,
    messageCount: apiConv.message_count || 0,
    totalTokensUsed: 0,
    // Older rows/endpoints did not project personal organization. Treat that
    // legacy absence as an unorganized conversation instead of leaking stale UI
    // state from another query snapshot.
    isArchived: apiConv.is_archived ?? false,
    isPinned: apiConv.is_pinned ?? false,
    pinnedAt: asDate(apiConv.pinned_at),
    archivedAt: asDate(apiConv.archived_at),
    archiveOrigin: apiConv.archive_origin ?? null,
    tags: [],
    createdAt: new Date(apiConv.created_at),
    updatedAt: new Date(apiConv.updated_at || apiConv.created_at),
    // `/conversations/global` is ordered by last_activity; the sidebar re-sorts
    // locally after merging owned + shared rows, so it must use the same activity
    // clock rather than metadata updated_at.
    lastMessageAt: new Date(apiConv.last_activity || apiConv.updated_at || apiConv.created_at),
    sourceSystem: apiConv.source_system || undefined,
    // Resource-sharing (P4) : ownership vit dans `user_id` (pas de colonne dédiée,
    // cf. M135). `owner_id` est un alias serveur — on retombe sur `user_id` sinon.
    ownerId: apiConv.owner_id ?? apiConv.user_id ?? null,
    visibility: apiConv.visibility,
    my_role: apiConv.my_role,
    // Partages nominatifs (owner-only) — remontés tels quels pour l'affichage
    // des avatars des destinataires dans la liste (à côté du glyphe source).
    shares: apiConv.shares,
  };
}

/**
 * Transform API message format to ChatMessage format
 */
function transformMessage(apiMsg: Message): ChatMessage {
  return {
    id: apiMsg.id,
    conversationId: apiMsg.conversation_id,
    role: apiMsg.role,
    content: apiMsg.content,
    citations: apiMsg.citations,
    sources: apiMsg.sources,
    metadata: apiMsg.metadata,
    createdAt: new Date(apiMsg.created_at),
    isStreaming: false,
    streamingComplete: true,
  };
}

/**
 * Hook pour récupérer toutes les conversations (global)
 * Migrated to Backend API
 */
export function useGlobalConversations(options: GlobalConversationsOptions = {}) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { data: currentCabinet } = useCurrentCabinet();
  const cabinetId = currentCabinet?.id ?? null;
  const archived = options.archived ?? "exclude";

  return useQuery({
    queryKey: globalConversationsQueryKey(cabinetId ?? "unknown-cabinet", userId ?? "unknown-user", archived),
    queryFn: async () => {
      if (!userId || !cabinetId) return [];

      // La LISTE des conversations est la donnée PRIMAIRE. Ne JAMAIS la remplacer
      // par [] sur erreur : après un envoi, un `invalidateQueries(['conversations'])`
      // force un refetch réseau immédiat ; si CE refetch échoue transitoirement
      // (401 pendant un refresh de token, 5xx, blip réseau), un `return []` viderait
      // toute la sidebar — y compris les fils PARTAGÉS avec moi, qui n'ont aucun
      // phantom de secours (contrairement à un fil que je crée) → ils disparaissent,
      // et `my_role` devient `undefined` → le composer lecture-seule se réactive
      // (incident 2026-07-07). On re-throw : TanStack conserve la dernière liste
      // réussie et refetch en arrière-plan.
      let conversations: Conversation[];
      try {
        conversations = await apiClient.get<Conversation[]>(`/conversations/global?archived=${archived}`);
      } catch (error) {
        ErrorService.handle(error, { context: { action: 'fetch_global_conversations' }, silent: true });
        throw error;
      }

      return conversations.map(transformConversation);
    },
    // Resource-sharing (2026-07-07) : la sidebar conversations est montée dans le
    // chrome global, donc elle survit aux logout/login rapides. Sans userId dans
    // la query key et sans `enabled`, un fetch parti sans session (401) ou avec le
    // token du compte précédent peut laisser le compte suivant avec une liste
    // vide/errored, alors que `/conversations/global` renvoie bien ses partages.
    enabled: !!userId && !!cabinetId,
    staleTime: 30000,
    // Sharing is cross-session: Mathieu can grant Marcus access while Marcus'
    // sidebar already has a fresh cached list. Until share notifications/realtime
    // exist, always refetch on mount/focus so received conversations appear
    // without requiring a manual hard refresh.
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
}

function mergeOrganizationStateField(
  conversation: ChatConversation,
  state: ResolvedConversationUserState,
  field: OrganizationField,
): ChatConversation {
  if (field === "pin") {
    return {
      ...conversation,
      isPinned: state.is_pinned,
      pinnedAt: asDate(state.pinned_at),
    };
  }
  return {
    ...conversation,
    isArchived: state.is_archived,
    archivedAt: asDate(state.archived_at),
    archiveOrigin: state.archive_origin,
  };
}

function optimisticPinnedState(
  conversation: ChatConversation,
  pinned: boolean,
): ChatConversation {
  return {
    ...conversation,
    isPinned: pinned,
    pinnedAt: pinned ? new Date() : null,
    // Pinning is personal organization only. In particular, it MUST NOT touch
    // lastMessageAt: otherwise a pin would falsely make an old thread recent.
  };
}

function optimisticArchivedState(
  conversation: ChatConversation,
  archived: boolean,
): ChatConversation {
  return {
    ...conversation,
    isArchived: archived,
    archivedAt: archived ? new Date() : null,
    archiveOrigin: archived ? "manual" : null,
    // Keep personal pin metadata intact. The backend also preserves it, so an
    // archive/unarchive round-trip never loses the user's pin.
  };
}

function isEligibleForArchiveProjection(
  conversation: ChatConversation,
  archiveFilter: ConversationArchiveFilter,
): boolean {
  return archiveFilter === "include"
    || (archiveFilter === "exclude" ? !conversation.isArchived : conversation.isArchived);
}

function insertConversationInProjection(
  conversations: ChatConversation[],
  conversation: ChatConversation,
): ChatConversation[] {
  const withoutConversation = conversations.filter(({ id }) => id !== conversation.id);
  const insertionIndex = withoutConversation.findIndex(
    (candidate) => compareConversationProjectionOrder(conversation, candidate) < 0,
  );
  if (insertionIndex === -1) return [...withoutConversation, conversation];
  return [
    ...withoutConversation.slice(0, insertionIndex),
    conversation,
    ...withoutConversation.slice(insertionIndex),
  ];
}

function findConversationInSnapshots(
  snapshots: Array<{ conversations: ChatConversation[] }>,
  conversationId: string,
): ChatConversation | undefined {
  return snapshots
    .flatMap(({ conversations }) => conversations)
    .find(({ id }) => id === conversationId);
}

function updateGlobalConversationSnapshots(
  queryClient: ReturnType<typeof useQueryClient>,
  scopeKey: QueryKey | null,
  conversationId: string,
  field: OrganizationField,
  update: (conversation: ChatConversation) => ChatConversation,
  sourceFallback?: ChatConversation,
): void {
  if (!scopeKey) return;

  const snapshots = queryClient.getQueriesData<ChatConversation[]>({ queryKey: scopeKey })
    .filter((snapshot): snapshot is [QueryKey, ChatConversation[]] => snapshot[1] !== undefined)
    .flatMap(([queryKey, conversations]) => {
      const archiveFilter = archiveFilterForQueryKey(queryKey);
      return archiveFilter ? [{ queryKey, conversations, archiveFilter }] : [];
    });
  const sourceConversation = findConversationInSnapshots(snapshots, conversationId) ?? sourceFallback;
  if (!sourceConversation) return;

  for (const { queryKey, conversations, archiveFilter } of snapshots) {
    const currentConversation = conversations.find(({ id }) => id === conversationId);
    if (field === "pin") {
      // Pinning never changes archive eligibility. Updating only rows already in
      // a projection prevents a pin response from resurrecting a row that an
      // archive mutation has deliberately moved out of that projection. Reinsert
      // present rows through the canonical comparator so active and include
      // projections immediately reflect pin/unpin priority without crossing the
      // archive boundary.
      const next = currentConversation
        ? insertConversationInProjection(conversations, update(currentConversation))
        : conversations;
      queryClient.setQueryData(queryKey, next);
      continue;
    }

    // Archive is the sole owner of projection membership. When the row is not
    // present locally, source it from another warm projection so an unarchive
    // immediately re-enters exclude (and archive enters only) without waiting
    // for the reconciliation refetch.
    const changedConversation = update(currentConversation ?? sourceConversation);
    const next = isEligibleForArchiveProjection(changedConversation, archiveFilter)
      ? insertConversationInProjection(conversations, changedConversation)
      : conversations.filter(({ id }) => id !== conversationId);
    queryClient.setQueryData(queryKey, next);
  }
}

function organizationStateFromConversation(conversation: ChatConversation): ResolvedConversationUserState {
  return {
    conversation_id: conversation.id,
    is_pinned: conversation.isPinned,
    pinned_at: conversation.pinnedAt?.toISOString() ?? null,
    is_archived: conversation.isArchived,
    archived_at: conversation.archivedAt?.toISOString() ?? null,
    archive_origin: conversation.archiveOrigin ?? null,
  };
}

function updateConversationDetailArchiveState(
  queryClient: ReturnType<typeof useQueryClient>,
  detailQueryKey: QueryKey | null,
  conversationId: string,
  state: ResolvedConversationUserState,
): void {
  if (!detailQueryKey) return;
  queryClient.setQueryData<Conversation | null>(
    detailQueryKey,
    (detail) => {
      if (!detail || detail.id !== conversationId) return detail;
      return {
        ...detail,
        is_archived: state.is_archived,
        archived_at: state.archived_at,
        archive_origin: state.archive_origin,
      };
    },
  );
}

function useConversationOrganizationMutation<TInput extends OrganizationMutationInput>(
  field: OrganizationField,
  endpoint: (input: TInput) => string,
  body: (input: TInput) => { pinned: boolean } | { archived: boolean },
  optimisticUpdate: (conversation: ChatConversation, input: TInput) => ChatConversation,
) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: currentCabinet } = useCurrentCabinet();
  const userId = user?.id ?? null;
  const cabinetId = currentCabinet?.id ?? null;
  const scopeKey = cabinetId && userId ? globalConversationsScopeKey(cabinetId, userId) : null;
  const generationStates = getOrganizationMutationGenerations(queryClient);
  const pendingMutations = getOrganizationPendingMutations(queryClient);

  const mutationScopeKeyFor = (conversationId: string) =>
    `${cabinetId ?? "unknown-cabinet"}:${userId ?? "unknown-user"}:${conversationId}`;

  return useMutation<ResolvedConversationUserState, Error, TInput, OrganizationMutationContext>({
    mutationFn: async (input) => apiClient.patch<ResolvedConversationUserState>(endpoint(input), body(input)),
    onMutate: async (input) => {
      const mutationScopeKey = mutationScopeKeyFor(input.conversationId);
      const detailQueryKey = cabinetId && userId
        ? conversationDetailQueryKey(cabinetId, userId, input.conversationId)
        : null;
      const generationKey = `${mutationScopeKey}:${field}`;
      const generation = startOrganizationMutationGeneration(generationStates, generationKey);
      const mutationKey = `${generationKey}:${generation}`;
      startOrganizationPendingMutation(pendingMutations, mutationScopeKey, mutationKey);
      if (!scopeKey) {
        return {
          previous: [],
          field,
          generation,
          generationKey,
          mutationScopeKey,
          mutationKey,
          queryScopeKey: null,
          detailQueryKey,
        };
      }
      await queryClient.cancelQueries({ queryKey: scopeKey });
      const previous = queryClient.getQueriesData<ChatConversation[]>({ queryKey: scopeKey });
      updateGlobalConversationSnapshots(
        queryClient,
        scopeKey,
        input.conversationId,
        field,
        (conversation) => optimisticUpdate(conversation, input),
      );
      return {
        previous,
        field,
        generation,
        generationKey,
        mutationScopeKey,
        mutationKey,
        queryScopeKey: scopeKey,
        detailQueryKey,
      };
    },
    onError: (_error, input, context) => {
      if (
        !context
        || !isLatestOrganizationMutationGeneration(
          generationStates,
          context.generationKey,
          context.generation,
        )
      ) return;
      const rollbackSource = context.previous
        .flatMap(([, conversations]) => conversations ?? [])
        .find(({ id }) => id === input.conversationId);
      if (!rollbackSource) return;
      const rollbackState = organizationStateFromConversation(rollbackSource);
      updateGlobalConversationSnapshots(
        queryClient,
        context.queryScopeKey,
        input.conversationId,
        context.field,
        (conversation) => mergeOrganizationStateField(conversation, rollbackState, context.field),
        rollbackSource,
      );
    },
    onSuccess: (state, input, context) => {
      // A stale completion must not overwrite the more recent optimistic state.
      // The final settlement reconciles cache and server state once every
      // organization mutation for this conversation has completed.
      if (
        !context
        || !isLatestOrganizationMutationGeneration(
          generationStates,
          context.generationKey,
          context.generation,
        )
      ) return;
      updateGlobalConversationSnapshots(
        queryClient,
        context.queryScopeKey,
        input.conversationId,
        context.field,
        (conversation) => mergeOrganizationStateField(conversation, state, context.field),
      );
      if (context.field === "archive") {
        updateConversationDetailArchiveState(
          queryClient,
          context.detailQueryKey,
          input.conversationId,
          state,
        );
      }
    },
    onSettled: (_data, _error, _input, context) => {
      if (!context) return;
      const isLastFieldMutation = settleOrganizationMutationGeneration(
        generationStates,
        context.generationKey,
        context.generation,
      );
      if (context.field === "archive" && isLastFieldMutation && context.detailQueryKey) {
        // Detail is a separate query from the global projections. A latest
        // restore can fail while an older archive later succeeds on the server;
        // generation guards correctly ignore that stale response, so only a
        // final exact refetch can make the open-thread recovery state converge.
        // Keep this detached from mutateAsync: a background read failure belongs
        // to the detail query and must never become an archive action failure.
        void queryClient.refetchQueries({
          queryKey: context.detailQueryKey,
          exact: true,
          type: "all",
        }).catch(() => undefined);
      }
      if (!settleOrganizationPendingMutation(
        pendingMutations,
        context.mutationScopeKey,
        context.mutationKey,
      )) return;
      // Reconciliation must wait for every pin/archive request for this scoped
      // conversation. Refetching after an earlier completion can otherwise
      // replace the other field's still-pending optimistic state with stale API
      // data. The final settlement retains the required detail, scoped, and
      // general invalidations exactly once. Marking them stale without implicit
      // refetch keeps reconciliation controlled: the explicit scoped refetch
      // below refreshes every initialized projection for this cabinet/user.
      if (context.detailQueryKey) {
        queryClient.invalidateQueries({
          queryKey: context.detailQueryKey,
          refetchType: "none",
        });
      }
      if (context.queryScopeKey) {
        queryClient.invalidateQueries({ queryKey: context.queryScopeKey, refetchType: "none" });
      }
      queryClient.invalidateQueries({ queryKey: ["conversations"], refetchType: "none" });
      if (context.queryScopeKey) {
        void queryClient.refetchQueries({
          queryKey: context.queryScopeKey,
          type: "all",
        }).catch(() => undefined);
      }
    },
  });
}

/** Set the authenticated user's personal pin state through the dedicated route. */
export function useSetConversationPinned() {
  return useConversationOrganizationMutation<SetConversationPinnedInput>(
    "pin",
    ({ conversationId }) => `/conversations/${conversationId}/pin`,
    ({ pinned }) => ({ pinned }),
    (conversation, { pinned }) => optimisticPinnedState(conversation, pinned),
  );
}

/** Set the authenticated user's personal archive state through the dedicated route. */
export function useSetConversationArchived() {
  return useConversationOrganizationMutation<SetConversationArchivedInput>(
    "archive",
    ({ conversationId }) => `/conversations/${conversationId}/archive`,
    ({ archived }) => ({ archived }),
    (conversation, { archived }) => optimisticArchivedState(conversation, archived),
  );
}

/**
 * Hook pour récupérer les conversations d'un dossier
 * Migrated to Backend API
 */
export function useConversations(caseId: string, caseTitle?: string) {
  return useQuery({
    queryKey: ["conversations", caseId],
    queryFn: async () => {
      if (!caseId) return [];
      
      try {
        const conversations = await apiClient.get<Conversation[]>(`/cases/${caseId}/conversations`);
        // Toutes les conversations de ce dossier partagent le même caseTitle
        return conversations.map((conv) => ({ ...transformConversation(conv), caseTitle: caseTitle || null }));
      } catch (error) {
        ErrorService.handle(error, { context: { action: 'fetch_conversations' }, silent: true });
        return [];
      }
    },
    enabled: !!caseId,
    staleTime: 30000,
  });
}

/**
 * Hook pour récupérer une conversation spécifique
 * Migrated to Backend API
 */
export function useConversation(conversationId: string, options?: { enabled?: boolean }) {
  const { user } = useAuth();
  const { data: currentCabinet } = useCurrentCabinet();
  const userId = user?.id ?? null;
  const cabinetId = currentCabinet?.id ?? null;
  return useQuery({
    queryKey: conversationDetailQueryKey(
      cabinetId ?? "unknown-cabinet",
      userId ?? "unknown-user",
      conversationId,
    ),
    queryFn: async () => {
      if (!conversationId) return null;
      
      try {
        return await apiClient.get<Conversation>(`/conversations/${conversationId}`);
      } catch (error) {
        ErrorService.handle(error, { context: { action: 'fetch_conversation' }, silent: true });
        throw error;
      }
    },
    enabled: !!conversationId && !!cabinetId && !!userId && options?.enabled !== false,
  });
}

/**
 * Hook pour créer une conversation
 * Migrated to Backend API
 */
export function useCreateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateConversationRequest) => {
      const apiConversation = await apiClient.post<Conversation>('/conversations', data);
      return transformConversation(apiConversation);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["conversations", "global"] });
      if (variables.case_id) {
        queryClient.invalidateQueries({ queryKey: ["conversations", variables.case_id] });
      }
    },
  });
}

/**
 * Hook pour mettre à jour une conversation
 * Migrated to Backend API
 *
 * Optimistic update : seules les projections globales du cabinet et de
 * l'utilisateur courants sont mutées. Rollback sur erreur via le snapshot
 * capturé dans `onMutate`.
 */
export function useUpdateConversation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: currentCabinet } = useCurrentCabinet();
  const userId = user?.id ?? null;
  const cabinetId = currentCabinet?.id ?? null;
  const queryScopeKey = cabinetId && userId
    ? globalConversationsScopeKey(cabinetId, userId)
    : null;

  return useMutation({
    mutationFn: async ({ conversationId, data }: {
      conversationId: string;
      data: RenameConversationRequest;
    }) => {
      return await apiClient.patch<Conversation>(`/conversations/${conversationId}`, data);
    },
    onMutate: async ({ conversationId, data }) => {
      const detailKey = cabinetId && userId
        ? conversationDetailQueryKey(cabinetId, userId, conversationId)
        : null;
      if (!queryScopeKey) return { previous: [], queryScopeKey, detailKey };
      await queryClient.cancelQueries({ queryKey: queryScopeKey });
      const previous = queryClient.getQueriesData<ChatConversation[]>({ queryKey: queryScopeKey });
      queryClient.setQueriesData<ChatConversation[]>({ queryKey: queryScopeKey }, (old) =>
        old?.map((conversation) => (
          conversation.id === conversationId
            ? { ...conversation, title: data.conversation_title }
            : conversation
        )),
      );
      return { previous, queryScopeKey, detailKey };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        for (const [queryKey, data] of context.previous) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSuccess: (data, _variables, context) => {
      if (context?.queryScopeKey) {
        queryClient.invalidateQueries({ queryKey: context.queryScopeKey });
      }
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      if (context?.detailKey) {
        queryClient.setQueryData<Conversation>(context.detailKey, (current) => (
          current
            ? {
                ...current,
                conversation_title: data.conversation_title,
                updated_at: data.updated_at,
                last_activity: data.last_activity,
              }
            : current
        ));
        queryClient.invalidateQueries({ queryKey: context.detailKey });
      }
    },
  });
}

/**
 * Hook pour supprimer une conversation
 * Migrated to Backend API
 *
 * `removeQueries` sur la clé messages cabinet/user/conversation : sinon les
 * messages supprimés restent en cache et peuvent s'afficher brièvement si un
 * composant se remonte avant la purge normale des queries inactives.
 */
export function useDeleteConversation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: currentCabinet } = useCurrentCabinet();
  const userId = user?.id ?? null;
  const cabinetId = currentCabinet?.id ?? null;

  return useMutation({
    mutationFn: async (conversationId: string) => {
      await apiClient.delete(`/conversations/${conversationId}`);
      return conversationId;
    },
    onSuccess: (conversationId) => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      if (cabinetId && userId) {
        queryClient.removeQueries({
          queryKey: conversationMessagesQueryKey(cabinetId, userId, conversationId),
        });
      }
    },
  });
}

/**
 * Hook pour récupérer les messages d'une conversation
 * Migrated to Backend API
 */
export function useMessages(conversationId: string) {
  const { user } = useAuth();
  const { data: currentCabinet } = useCurrentCabinet();
  const userId = user?.id ?? null;
  const cabinetId = currentCabinet?.id ?? null;
  return useQuery({
    queryKey: conversationMessagesQueryKey(
      cabinetId ?? "unknown-cabinet",
      userId ?? "unknown-user",
      conversationId,
    ),
    queryFn: async () => {
      if (!conversationId) return [];
      
      try {
        const messages = await apiClient.get<Message[]>(`/conversations/${conversationId}/messages`);
        return messages.map(transformMessage);
      } catch (error) {
        ErrorService.handle(error, { context: { action: 'fetch_messages' }, silent: true });
        return [];
      }
    },
    enabled: !!conversationId && !!cabinetId && !!userId,
    staleTime: 10000,
    refetchInterval: (query) => {
      // React Query v5 : le callback reçoit le Query, pas le data directement.
      // Auto-refresh si moins de 10 messages (conversation active)
      const messages = query.state.data;
      return messages && messages.length < 10 ? 5000 : false;
    },
  });
}

/**
 * Hook pour créer un message
 * Migrated to Backend API
 */
export function useCreateMessage(conversationId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: currentCabinet } = useCurrentCabinet();
  const userId = user?.id ?? null;
  const cabinetId = currentCabinet?.id ?? null;

  return useMutation({
    mutationFn: async (data: CreateMessageRequest) => {
      return await apiClient.post<Message>(`/conversations/${conversationId}/messages`, data);
    },
    onSuccess: () => {
      if (cabinetId && userId) {
        queryClient.invalidateQueries({
          queryKey: conversationMessagesQueryKey(cabinetId, userId, conversationId),
        });
      }
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}
